import { memo, useMemo, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { PublicView, PlayerPublic, RoleId } from '@ringmaster/shared';
import { ROLE_META } from '@ringmaster/shared';
import { BroadcastFrame, SAFE } from './BroadcastFrame.js';
import { ProsceniumFrame } from './ProsceniumFrame.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { useSequence } from '../fx/sequencer.js';
import { NameCard } from './components/index.js';
import { ShakeContainer, BurstLayer, burst, ParticleLayer, type ShakeHandle } from '../fx/index.js';
import { Glyph, type GlyphName, type GlyphTone } from '../ui/Glyph.js';

/**
 * Stage EndScreen — the victory reveal (END phase), WIN_REVEAL sequence.
 *
 * beat banner: the winning faction's banner stamps in (gold "Village" / blood
 * "Wolves"). beat burst: a faction burst (goldBurst / bloodBurst) from the lower
 * corners + a shake. beat truthGrid: the full role grid reveals, staggered by
 * gridStaggerMs — every player's true role + match points, the "here's who
 * everyone really was" payoff.
 *
 * Two exports: EndScreenContent (inner) + EndScreenDemo (self-framed for CardLab).
 * Back-compat EndScreen keeps the old {view, caption, onPlayAgain, isHost}
 * signature so StageView compiles untouched (the Play Again control is a host/hand
 * concern, not part of this broadcast reveal — accepted-and-ignored here).
 */

export interface EndScreenProps {
  view: PublicView;
  isHost: boolean;
}

function EndScreenContentImpl({ view }: EndScreenProps) {
  const reduced = useReducedMotion();
  const result = view.result;
  const shakeRef = useRef<ShakeHandle>(null);

  const isVillageWin = result?.winner === 'VILLAGE';
  const atDawn = result?.atDawn ?? false;

  const onWin = useMemo(
    () => ({
      burst: () => {
        if (reduced) return;
        const preset = isVillageWin ? 'goldBurst' : 'bloodBurst';
        burst({ preset, x: 0.3, y: 0.5 });
        burst({ preset, x: 0.7, y: 0.5 });
        shakeRef.current?.shake(isVillageWin ? 0.5 : 0.7);
      },
    }),
    [reduced, isVillageWin],
  );

  const win = useSequence('WIN_REVEAL', { enabled: !!result, onCue: onWin });
  const showBanner = win.has('banner');
  const showGrid = win.has('truthGrid');
  const gridStagger = (win.params.gridStaggerMs ?? 90) / 1000;

  if (!result) return null;

  const bannerText = isVillageWin
    ? atDawn
      ? 'The Village Triumphs'
      : 'The Village Prevails'
    : atDawn
      ? 'The Wolves Claim the Dawn'
      : 'The Wolves Have Won';

  return (
    <ShakeContainer ref={shakeRef} style={{ position: 'absolute', inset: 0 }}>
      <BurstLayer />
      {/* Ambient victory motes: gold for the village, blood for the wolves. */}
      <ParticleLayer preset={isVillageWin ? 'goldDust' : 'bloodMotes'} intensity={0.7} slot={2} />

      {/* Winner banner, upper third. */}
      <div
        style={{
          position: 'absolute',
          top: SAFE + 60,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 4,
          opacity: showBanner ? 1 : 0,
        }}
      >
        {showBanner && (
          <NameCard
            name={bannerText}
            eyebrow={isVillageWin ? 'Victory' : 'The pack prevails'}
            variant={isVillageWin ? 'neutral' : 'condemned'}
          />
        )}
      </div>

      {/* Truth grid, lower two-thirds. Anchored above the reserved caption band so
          the plate never overlaps the cards: bottom:176 puts the grid's bottom edge
          at ≈ y=904, clearing the y≈920 line the LowerThird plate occupies. */}
      {showGrid && (
        <div
          style={{
            position: 'absolute',
            left: '10%',
            right: '10%',
            bottom: 176,
            zIndex: 4,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              marginBottom: 24,
              fontFamily: 'var(--font-ui, Inter, sans-serif)',
              fontSize: 'var(--stage-text-meta, 24px)',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--gold-4, #c9923f)',
            }}
          >
            <Glyph name="scroll" tone="gold" size={26} /> The full truth
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(view.players.length, 6)}, 1fr)`,
              gap: 20,
              justifyItems: 'center',
            }}
          >
            {view.players.map((player, idx) => (
              <TruthCard
                key={player.id}
                player={player}
                role={player.revealedRole ?? result.roles[player.id] ?? null}
                delay={reduced ? 0 : idx * gridStagger}
                reduced={!!reduced}
              />
            ))}
          </div>
        </div>
      )}
    </ShakeContainer>
  );
}

/** One player in the truth grid: role glyph + name + role label + score. */
function TruthCard({
  player,
  role,
  delay,
  reduced,
}: {
  player: PlayerPublic;
  role: RoleId | null;
  delay: number;
  reduced: boolean;
}) {
  const isWolf = role === 'WEREWOLF';
  const glyph: GlyphName = isWolf ? 'wolfHead' : role === 'SEER' ? 'seerEye' : 'candle';
  const tone: GlyphTone = isWolf ? 'blood' : 'gold';
  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24, rotateX: -30 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ delay, duration: reduced ? 0 : 0.45, ease: [0.7, 0, 0.16, 1] }}
      style={{
        width: '100%',
        maxWidth: 200,
        padding: '20px 16px',
        borderRadius: 'var(--radius-lg, 16px)',
        border: `2px solid ${isWolf ? 'var(--blood-5, #c22f3e)' : 'var(--gold-4, #c9923f)'}`,
        background: isWolf ? 'rgba(102,16,31,0.35)' : 'rgba(21,13,15,0.7)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        opacity: player.alive ? 1 : 0.72,
      }}
    >
      <Glyph name={glyph} tone={tone} size={48} />
      <span
        style={{
          fontFamily: 'var(--font-display, serif)',
          fontSize: 28,
          color: 'var(--text-primary, #fff)',
          textDecoration: player.alive ? 'none' : 'line-through',
        }}
      >
        {player.name}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-ui, Inter, sans-serif)',
          fontSize: 15,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: isWolf ? 'var(--blood-6, #ff5a4f)' : 'var(--gold-5, #e6b866)',
        }}
      >
        {role ? ROLE_META[role].label : '—'}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-ui, Inter, sans-serif)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 16,
          fontWeight: 700,
          color: player.score > 0 ? 'var(--gold-5, #e6b866)' : 'var(--state-dead, #9c8f86)',
        }}
      >
        {player.score} pt{player.score === 1 ? '' : 's'}
      </span>
    </motion.div>
  );
}

export const EndScreenContent = memo(EndScreenContentImpl);

/** CardLab / standalone — mounts the full frame stack (END_VILLAGE/END_WOLVES). */
export function EndScreenDemo({ view, isHost }: EndScreenProps) {
  const manifest = scenesForGame('bloodmoon');
  const winner = view.result?.winner;
  const inner = <EndScreenContent view={view} isHost={isHost} />;
  return (
    <BroadcastFrame>
      {manifest ? (
        // SceneStack already crossfades internally on scene-key change (END →
        // END_WOLVES), so it needs no outer key. Adding one would remount the
        // whole subtree — and the reveal — on a winner flip.
        <SceneStack manifest={manifest} phase={view.phase} winner={winner}>
          <ProsceniumFrame>{inner}</ProsceniumFrame>
        </SceneStack>
      ) : (
        <ProsceniumFrame>{inner}</ProsceniumFrame>
      )}
    </BroadcastFrame>
  );
}
